/**
 * AGR-08 — agreement LIFECYCLE actions (renew / cancel / transfer / create),
 * the layer above AGR-03b's per-check management. ESC's `mirror_serv_agr` is
 * read-only (DEC-22), so these decisions are recorded Pegasus-native in
 * `agreement_action` (DEC-25: build the write-machinery now on a Pegasus table;
 * sync to ESC at cutover). Every action is undoable + carries who/when/why.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type AgreementActionType =
  | "renew"
  | "cancel"
  | "transfer"
  | "create"
  | "note";

export interface AgreementActionRow {
  id: string;
  agreement_no: string;
  custno: string | null;
  action_type: AgreementActionType;
  reason: string | null;
  detail: Record<string, unknown> | null;
  effective_date: string | null;
  performed_by: string | null;
  created_at: string;
  undone_at: string | null;
}

/** Live (not-undone) actions for an agreement, newest first. */
export function useAgreementActions(agreementNo: string | undefined) {
  return useQuery({
    queryKey: ["agreement_actions", agreementNo],
    enabled: !!agreementNo,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AgreementActionRow[]> => {
      const { data, error } = await supabase
        .from("agreement_action")
        .select("*")
        .eq("agreement_no", agreementNo!)
        .is("undone_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AgreementActionRow[];
    },
  });
}

export interface RecordActionInput {
  agreementNo: string;
  custno: string | null;
  actionType: AgreementActionType;
  reason?: string | null;
  detail?: Record<string, unknown> | null;
  effectiveDate?: string | null;
}

export function useRecordAgreementAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: RecordActionInput) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("agreement_action").insert({
        agreement_no: v.agreementNo,
        custno: v.custno,
        action_type: v.actionType,
        reason: v.reason ?? null,
        detail: v.detail ?? null,
        effective_date:
          v.effectiveDate ?? new Date().toISOString().slice(0, 10),
        performed_by: userData?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["agreement_actions", v.agreementNo] }),
  });
}

/** Undo = soft-delete (stamp undone_at) so the audit trail survives. */
export function useUndoAgreementAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; agreementNo: string }) => {
      const { error } = await supabase
        .from("agreement_action")
        .update({ undone_at: new Date().toISOString() })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["agreement_actions", v.agreementNo] }),
  });
}

/** expire_date + the contract period ("1 Year" / "2 Year") → the renewed-to date. */
export function addContractPeriod(
  dateISO: string | null,
  contrPer: string | null,
): string | null {
  if (!dateISO) return null;
  const m = contrPer?.match(/(\d+)\s*year/i);
  const years = m ? parseInt(m[1], 10) : 1;
  const d = new Date(dateISO);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}
